import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  AnalysisSettings,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSettingsPair,
  ProfileDefaults,
} from '@paragon/contract-library';
import { componentDesignsDeletedWithInsertions } from '@paragon/layout-models';

export interface SettingsDatabaseState {
  readonly analysisSettings: {
    readonly [modelGuid: string]: AnalysisSettings;
  };
  readonly loadSettings: {
    readonly [modelGuid: string]: LoadSettings;
  };
  readonly materialSettings: {
    readonly [modelGuid: string]: MaterialSettingsPair;
  };
  readonly featurePatternSettings: {
    readonly [modelGuid: string]: FeaturePatternTrussSettings;
  };
  readonly profileDefaults: {
    readonly [modelGuid: string]: ProfileDefaults;
  };
}

const initialState: Readonly<SettingsDatabaseState> = {
  analysisSettings: {},
  loadSettings: {},
  materialSettings: {},
  featurePatternSettings: {},
  profileDefaults: {},
};

const settingsDatabaseSlice = createSlice({
  name: 'settingsDatabase',
  initialState,
  reducers: {
    setAnalysisSettings: (
      state,
      action: PayloadAction<{ modelGuids: string[]; analysisSettings: AnalysisSettings }>,
    ) => {
      for (const modelGuid of action.payload.modelGuids) {
        state.analysisSettings[modelGuid] = action.payload.analysisSettings;
      }
    },
    setAnalysisSettingsFromObject: (
      state,
      action: PayloadAction<{ readonly [componentDesignGuid: string]: AnalysisSettings }>,
    ) => {
      for (const [componentDesignGuid, analysisSettings] of Object.entries(action.payload)) {
        state.analysisSettings[componentDesignGuid] = castDraft(analysisSettings);
      }
    },
    setLoadSettings: (state, action: PayloadAction<{ modelGuids: string[]; loadSettings: LoadSettings }>) => {
      for (const modelGuid of action.payload.modelGuids) {
        state.loadSettings[modelGuid] = castDraft(action.payload.loadSettings);
      }
    },
    setLoadSettingsFromObject: (
      state,
      action: PayloadAction<{ readonly [componentDesignGuid: string]: LoadSettings }>,
    ) => {
      for (const [componentDesignGuid, loadSettings] of Object.entries(action.payload)) {
        state.loadSettings[componentDesignGuid] = castDraft(loadSettings);
      }
    },
    setMaterialSettings: (
      state,
      action: PayloadAction<{ modelGuids: string[]; materialSettings: MaterialSettingsPair }>,
    ) => {
      for (const modelGuid of action.payload.modelGuids) {
        state.materialSettings[modelGuid] = castDraft(action.payload.materialSettings);
      }
    },
    setFeaturePatternSettings: (
      state,
      action: PayloadAction<{ modelGuids: string[]; featurePatternSettings: FeaturePatternTrussSettings }>,
    ) => {
      for (const modelGuid of action.payload.modelGuids) {
        state.featurePatternSettings[modelGuid] = castDraft(action.payload.featurePatternSettings);
      }
    },
    setProfileDefaults: (state, action: PayloadAction<{ modelGuids: string[]; profileDefaults: ProfileDefaults }>) => {
      for (const modelGuid of action.payload.modelGuids) {
        state.profileDefaults[modelGuid] = castDraft(action.payload.profileDefaults);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const componentDesignGuid of action.payload.guids) {
        delete state.analysisSettings[componentDesignGuid];
        delete state.loadSettings[componentDesignGuid];
        delete state.materialSettings[componentDesignGuid];
        delete state.featurePatternSettings[componentDesignGuid];
        delete state.profileDefaults[componentDesignGuid];
      }
    });
  },
});

export const {
  setAnalysisSettings,
  setAnalysisSettingsFromObject,
  setLoadSettings,
  setLoadSettingsFromObject,
  setMaterialSettings,
  setFeaturePatternSettings,
  setProfileDefaults,
} = settingsDatabaseSlice.actions;
export const settingsDatabase = settingsDatabaseSlice.reducer;
